import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { visitsApi } from '../api/visits.api';
import { CreateVisitDto } from '../types/visit.types';

export const useVisit = (id: number) => {
  return useQuery({
    queryKey: ['visits', id],
    queryFn: () => visitsApi.getById(id),
    enabled: !!id && id > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache for 10 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

export const useVisitsByPatientId = (patientId: number) => {
  return useQuery({
    queryKey: ['visits', 'patient', patientId],
    queryFn: () => visitsApi.getByPatientId(patientId),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache for 10 minutes
  });
};

export const useCreateVisit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVisitDto) => visitsApi.create(data),
    onSuccess: (newVisit, variables) => {
      // Set the new visit in cache immediately to avoid refetch
      queryClient.setQueryData(['visits', newVisit.id], newVisit);
      // Only invalidate patient visits list (not the individual visit we just created)
      queryClient.invalidateQueries({ 
        queryKey: ['visits', 'patient', variables.patientId],
        exact: true, // Only invalidate exact match
      });
      queryClient.invalidateQueries({
        queryKey: ['visits', 'patient', variables.patientId, 'all-files'],
        exact: true, // Only invalidate exact match
      });
    },
  });
};

export const useVisitFiles = (visitId: number) => {
  return useQuery({
    queryKey: ['visits', visitId, 'files'],
    queryFn: () => visitsApi.getVisitFiles(visitId),
    enabled: !!visitId,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache for 10 minutes
  });
};

export const useAllVisitFiles = (patientId: number) => {
  return useQuery({
    queryKey: ['visits', 'patient', patientId, 'all-files'],
    queryFn: () => visitsApi.getAllVisitFiles(patientId),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache for 10 minutes
  });
};

export const useDeleteVisitFile = (patientId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: number) => visitsApi.deleteVisitFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['visits', 'patient', patientId, 'all-files'],
      });
    },
  });
};

